/**
 * Lazy Section Loader for Whisk Hospitality Theme
 * Uses IntersectionObserver to load below-fold sections on demand
 * Reduces initial page weight and improves LCP
 */

(function() {
  'use strict';

  // Early exit if IntersectionObserver not supported
  if (!('IntersectionObserver' in window)) {
    console.warn('IntersectionObserver not supported - loading all sections immediately');
    loadAllSections();
    return;
  }

  // Store loaded sections to prevent duplicate loads
  const loadedSections = new Set();

  // Store section content cache
  const sectionCache = new Map();

  /**
   * Initialize lazy loading for all lazy sections
   */
  function initLazySections() {
    const lazySections = document.querySelectorAll('.lazy-section');

    if (lazySections.length === 0) {
      return;
    }

    lazySections.forEach(function(section) {
      const sectionId = section.dataset.sectionId;
      const threshold = parseInt(section.dataset.threshold) || 200;
      const rootMargin = threshold + 'px';

      // Create observer for this section
      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting && !loadedSections.has(sectionId)) {
            loadSection(section, sectionId);
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: rootMargin,
        threshold: 0.01
      });

      observer.observe(section);
    });
  }

  /**
   * Load a specific section
   * @param {HTMLElement} container - The lazy section container
   * @param {string} sectionId - Section identifier
   */
  function loadSection(container, sectionId) {
    if (loadedSections.has(sectionId)) {
      return;
    }

    loadedSections.add(sectionId);
    const sectionName = container.dataset.sectionName || 'Section';

    // Check cache first
    if (sectionCache.has(sectionId)) {
      renderSection(container, sectionCache.get(sectionId));
      return;
    }

    // Fetch section content via Section Rendering API
    const url = window.location.pathname + '?section_id=' + sectionId;

    fetch(url)
      .then(function(response) {
        if (!response.ok) {
          throw new Error('Failed to load section: ' + response.status);
        }
        return response.text();
      })
      .then(function(html) {
        // Cache the content
        sectionCache.set(sectionId, html);
        renderSection(container, html);
      })
      .catch(function(error) {
        console.error('Error loading section ' + sectionId + ':', error);
        handleLoadError(container, sectionName);
      });
  }

  /**
   * Render section content into container
   * @param {HTMLElement} container - The lazy section container
   * @param {string} html - Section HTML content
   */
  function renderSection(container, html) {
    // Create a temporary container to parse HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;

    // Extract the actual section content (remove any wrapper divs if present)
    const content = temp.firstElementChild || temp;

    // Replace placeholder with actual content
    container.innerHTML = '';
    container.style.minHeight = '';

    // Move all children from temp to container
    while (content.firstChild) {
      container.appendChild(content.firstChild);
    }

    // Remove lazy-section class
    container.classList.remove('lazy-section');

    // Dispatch custom event for section loaded
    const event = new CustomEvent('section:loaded', {
      detail: { container: container },
      bubbles: true
    });
    container.dispatchEvent(event);

    // Re-initialize any scripts in the loaded section
    initSectionScripts(container);
  }

  /**
   * Initialize scripts within a loaded section
   * @param {HTMLElement} container - The section container
   */
  function initSectionScripts(container) {
    // Re-initialize Splide carousels if present
    if (typeof Splide !== 'undefined') {
      const carousels = container.querySelectorAll('.splide:not(.is-initialized)');
      carousels.forEach(function(carousel) {
        try {
          new Splide(carousel).mount();
        } catch (error) {
          console.error('Error initializing carousel:', error);
        }
      });
    }

    // Trigger custom initialization event for theme scripts
    const event = new CustomEvent('section:init', {
      detail: { container: container },
      bubbles: true
    });
    container.dispatchEvent(event);
  }

  /**
   * Handle section load error
   * @param {HTMLElement} container - The lazy section container
   * @param {string} sectionName - Section name for display
   */
  function handleLoadError(container, sectionName) {
    const placeholder = container.querySelector('.lazy-section__placeholder');
    if (placeholder) {
      placeholder.innerHTML = '<p style="color: rgba(var(--text-color), 0.6); text-align: center;">Failed to load ' + sectionName + '. <a href="#" onclick="location.reload(); return false;" style="text-decoration: underline;">Refresh page</a></p>';
    }
  }

  /**
   * Fallback: Load all sections immediately (for unsupported browsers)
   */
  function loadAllSections() {
    const lazySections = document.querySelectorAll('.lazy-section');
    lazySections.forEach(function(section) {
      const sectionId = section.dataset.sectionId;
      loadSection(section, sectionId);
    });
  }

  /**
   * Prefetch sections on idle for faster subsequent navigation
   */
  function prefetchSectionsOnIdle() {
    if (!('requestIdleCallback' in window)) {
      return;
    }

    requestIdleCallback(function() {
      const lazySections = document.querySelectorAll('.lazy-section');
      lazySections.forEach(function(section) {
        const sectionId = section.dataset.sectionId;
        if (!loadedSections.has(sectionId)) {
          const url = window.location.pathname + '?section_id=' + sectionId;

          // Prefetch using link rel=prefetch
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = url;
          link.as = 'fetch';
          document.head.appendChild(link);
        }
      });
    }, { timeout: 10000 });
  }

  // Initialize on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initLazySections();
      prefetchSectionsOnIdle();
    });
  } else {
    initLazySections();
    prefetchSectionsOnIdle();
  }

  // Expose API for manual control if needed
  window.LazySectionLoader = {
    init: initLazySections,
    load: function(sectionId) {
      const container = document.querySelector('[data-section-id="' + sectionId + '"]');
      if (container) {
        loadSection(container, sectionId);
      }
    }
  };
})();
